/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 389 lines
 * - Adding: ~12 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/features/inventory/EditInventoryModal.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createInventoryItem, updateInventoryItem } from "../../services/inventoryService";
import { CATEGORY_OPTIONS } from "../../constants/inventoryOptions";

// [2026-02-13] CURRENCY_PHASE1: Import Currency type for profile-based default
import { Currency } from "../../interfaces/Currency";

interface EditInventoryModalProps {
  show: boolean;
  onHide: () => void;
  initialItem: any | null;
  onSaved: () => void;
  // [2026-02-13] CURRENCY_PHASE1: User's preferred currency from profile for new items
  userCurrency?: Currency;
}

const EditInventoryModal: React.FC<EditInventoryModalProps> = ({
  show,
  onHide,
  initialItem,
  onSaved,
  userCurrency = "USD",
}) => {
  // NOTE: Backend inventory model (server/models/Inventory.js) enforces that at least
  // one of `partNumber` or `model` is provided via a Mongoose pre-validate hook.
  // Keep this frontend schema in sync with that business rule.
  const schema = Yup.object({
    manufacturer: Yup.string().required("Required").min(2).max(80).trim(),
    partNumber: Yup.string()
      .max(80)
      .trim()
      .test("pn-or-model", "Either part number or model is required", function (value) {
        const { model } = this.parent as any;
        return !!(value || model);
      }),
    model: Yup.string()
      .max(80)
      .trim()
      .test("model-or-pn", "Either part number or model is required", function (value) {
        const { partNumber } = this.parent as any;
        return !!(value || partNumber);
      }),
    title: Yup.string().trim(),
    quantity: Yup.number().required("Required").min(1).integer(),
    condition: Yup.string()
      .required("Required")
      .oneOf([
        "NEW Retail",
        "New Bulk",
        "Refurbished",
        "Used",
        "AS-IS",
      ]),
    price: Yup.number().nullable().min(0),
    currency: Yup.string().oneOf(["USD", "EUR", "GBP"]).default("USD"),
    category: Yup.string()
      .oneOf([...CATEGORY_OPTIONS])
      .default("Other"),
    description: Yup.string().max(50).trim(),
    country: Yup.string().max(2).trim(),
    isActive: Yup.boolean().default(true),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialItem
      ? {
          manufacturer: initialItem.manufacturer ?? "",
          partNumber: initialItem.partNumber ?? "",
          model: initialItem.model ?? "",
          title: initialItem.title ?? "",
          quantity: initialItem.quantity ?? 1,
          condition: initialItem.condition ?? "",
          price: initialItem.price ?? "",
          currency: initialItem.currency ?? "USD",
          category: initialItem.category ?? "Other",
          description: initialItem.description ?? "",
          country: initialItem.country ?? "",
          isActive: initialItem.isActive !== false,
        }
      : {
          manufacturer: "",
          partNumber: "",
          model: "",
          title: "",
          quantity: 1,
          condition: "",
          price: "",
          // [2026-02-13] CURRENCY_PHASE1: Default to user's profile currency for new items
          currency: userCurrency,
          category: "Other",
          description: "",
          country: "",
          isActive: true,
        },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        const isEditMode = !!initialItem?._id;

        if (isEditMode) {
          await updateInventoryItem(initialItem._id, values);
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.success("Your changes have been saved");
        } else {
          await createInventoryItem(values);
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.success("Item added successfully");
        }

        onHide();
        onSaved?.();
      } catch (e: any) {
        // [2025-01-XX] ui-standardization: Using centralized notify helper with error sanitization
        const status = e?.response?.status;
        const errorPayload = e?.response?.data;

        if (status === 404) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(e, "This item no longer exists");
          onHide();
          onSaved?.();
        } else if (status === 403) {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(e, "You don't have permission to edit this item");
        } else if (status === 400 && Array.isArray(errorPayload?.errors)) {
          // Map backend validation errors back into specific form fields when possible
          errorPayload.errors.forEach((err: any) => {
            if (err?.path && err?.message) {
              setFieldError(err.path, err.message);
            }
          });

          const firstMessage =
            errorPayload.errors.find((err: any) => err?.message)?.message ||
            "Please check the highlighted fields and try again";

          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(e, firstMessage);
        } else {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(e, "We couldn't save your changes. Please try again");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      centered
      className="erb-modal p-small"
    >
      <Modal.Header closeButton>
        <Modal.Title className="p-small">
          {initialItem && initialItem._id ? "Edit Inventory Item" : "Add Inventory Item"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit} className="erb-form p-small">
        <Modal.Body>
          <div className="row g-3 p-small">
            <div className="col-lg-4 col-md-6">
              <Form.Label className="p-small">Manufacturer *</Form.Label>
              <Form.Control
                className="p-small"
                name="manufacturer"
                value={formik.values.manufacturer}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.manufacturer && formik.errors.manufacturer)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.manufacturer as any}
              </Form.Control.Feedback>
            </div>
            <div className="col-lg-4 col-md-6">
              <Form.Label className="p-small">Part number</Form.Label>
              <Form.Control
                className="p-small"
                name="partNumber"
                value={formik.values.partNumber}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.partNumber && formik.errors.partNumber)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.partNumber as any}
              </Form.Control.Feedback>
            </div>
            <div className="col-lg-4 col-md-6">
              <Form.Label className="p-small">Model</Form.Label>
              <Form.Control
                className="p-small"
                name="model"
                value={formik.values.model}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.model && formik.errors.model)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.model as any}
              </Form.Control.Feedback>
            </div>
            <div className="col-12">
              <Form.Text className="text-muted p-small">
                At least one of <strong>Part number</strong> or <strong>Model</strong> is required.
              </Form.Text>
            </div>

            {/* --- Title full width --- */}
            <div className="col-12">
              <Form.Label className="p-small">Title</Form.Label>
              <Form.Control
                className="p-small"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.title && formik.errors.title)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.title as any}
              </Form.Control.Feedback>
            </div>

            {/* --- Category full width (new row) --- */}
            <div className="col-12">
              <Form.Label className="p-small">Category</Form.Label>
              <Form.Select
                className="p-small wide-select"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.category && formik.errors.category)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} title={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.category as any}
              </Form.Control.Feedback>
            </div>

            {/* --- Condition full width --- */}
            <div className="col-12">
              <Form.Label className="p-small">Condition *</Form.Label>
              <Form.Select
                className="p-small wide-select"
                name="condition"
                value={formik.values.condition || ""}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.condition && formik.errors.condition)}
              >
                <option value="" disabled hidden>
                  Select condition
                </option>
                {[
                  "NEW Retail",
                  "New Bulk",
                  "Refurbished",
                  "Used",
                  "AS-IS",
                ].map((opt) => (
                  <option key={opt} value={opt} title={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.condition as any}
              </Form.Control.Feedback>
            </div>

            {/* --- Quantity, Price, and Currency on the same line --- */}
            <div className="col-lg-4 col-md-4">
              <Form.Label className="p-small">Quantity *</Form.Label>
              <Form.Control
                className="p-small"
                type="number"
                name="quantity"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.quantity && formik.errors.quantity)}
                min={1}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.quantity as any}
              </Form.Control.Feedback>
            </div>

            <div className="col-lg-4 col-md-4">
              <Form.Label className="p-small">Price</Form.Label>
              <Form.Control
                className="p-small"
                type="number"
                step="0.01"
                name="price"
                value={formik.values.price as any}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.price && formik.errors.price)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.price as any}
              </Form.Control.Feedback>
            </div>

            <div className="col-lg-4 col-md-4">
              <Form.Label className="p-small">Currency</Form.Label>
              <Form.Control
                className="p-small form-control-readonly"
                name="currency"
                value={formik.values.currency}
                readOnly
              />
            </div>

            <div className="col-12">
              <Form.Label className="p-small">Description</Form.Label>
              <Form.Control
                className="p-small"
                as="textarea"
                rows={3}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.description && formik.errors.description)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.description as any}
              </Form.Control.Feedback>
            </div>

            <div className="col-12">
              <Form.Check
                className="p-small form-check-disabled"
                type="checkbox"
                name="isActive"
                label="Active"
                checked={formik.values.isActive}
                onChange={formik.handleChange}
                disabled
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between p-small">
          <Button type="button" className="btn-cancel-figma" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit" className="btn-black" disabled={formik.isSubmitting}>
            {initialItem && initialItem._id ? "Save changes" : "Add Item"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditInventoryModal;
